import { IWPImage, IWPLink } from "@/src/types";
import { MainSection } from "@/styles/global";
import React, { useRef, useCallback } from "react";
import S from "./SubCategoriesCards.styled";
import { getBgColor } from "@/src/helpers";
import ArrowIcon from "assets/arrow.svg";
import FilledArrow from "assets/filled-arrow.svg";

import {
  Scrollbar,
  FreeMode,
  Mousewheel,
  Navigation,
  Pagination,
  Grid,
} from "swiper/modules";
import SwiperCore from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.css";
import "swiper/css";
import "swiper/css/grid";
import "swiper/css/scrollbar";

SwiperCore.use([Navigation, Pagination, Scrollbar, Grid]);

export type SubCategoriesCardsProps = {
  title: string;
  smallCatCards: {
    categoryTitle: string;
    categoryLink: IWPLink;
    categoryImage: IWPImage;
    hasIcon: boolean;
  }[];
  categoryColor: string;
};

const SubCategoriesCards: React.FC<SubCategoriesCardsProps> = ({
  title,
  smallCatCards,
  categoryColor,
}) => {
  const sliderRef = useRef<React.ElementRef<typeof Swiper>>(null);
  const [isBeginning, setIsBeginning] = React.useState(true);
  const [isEnd, setIsEnd] = React.useState(false);

  React.useEffect(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.on("slideChange", () => {
      if (sliderRef.current) {
        setIsBeginning(sliderRef.current.swiper.isBeginning);
        setIsEnd(sliderRef.current.swiper.isEnd);
      }
    });
  }, [sliderRef]);

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slideNext();
  }, []);

  const renderCards = smallCatCards?.map(
    ({ categoryLink, categoryTitle, categoryImage, hasIcon }, index) => {
      return (
        <React.Fragment key={index}>
          {smallCatCards.length > 2 ? (
            <SwiperSlide key={index} className="swiperSlide">
              <S.Card
                $bgColor={getBgColor("dark", categoryColor)}
                to={categoryLink.url}
                target={categoryLink.target}
              >
                {categoryTitle && <S.CardTitle>{categoryTitle}</S.CardTitle>}
                <S.FlexWrapper>
                  <S.CardIcon>
                    <ArrowIcon />
                  </S.CardIcon>
                  {hasIcon && (
                    <S.CategoryIcon
                      src={categoryImage?.sourceUrl}
                      alt={categoryImage.altText}
                    />
                  )}
                </S.FlexWrapper>
              </S.Card>
            </SwiperSlide>
          ) : (
            <S.Card
              key={index}
              $bgColor={getBgColor("dark", categoryColor)}
              to={categoryLink.url}
              target={categoryLink.target}
            >
              {categoryTitle && <S.CardTitle>{categoryTitle}</S.CardTitle>}
              <S.FlexWrapper>
                <S.CardIcon>
                  <ArrowIcon />
                </S.CardIcon>
                {hasIcon && (
                  <S.CategoryIcon
                    src={categoryImage?.sourceUrl}
                    alt={categoryImage.altText}
                  />
                )}
              </S.FlexWrapper>
            </S.Card>
          )}
        </React.Fragment>
      );
    }
  );

  return (
    <MainSection>
      <S.Inner>
        {title && <S.Title>{title}</S.Title>}
        <S.CardsWrapper $array={smallCatCards.length}>
          <>
            {smallCatCards.length > 2 ? (
              <Swiper
                modules={[Scrollbar, FreeMode, Mousewheel, Grid]}
                ref={sliderRef}
                className="mySwiper"
                spaceBetween={16}
                grid={{
                  rows: 2,
                  fill: "row",
                }}
                slidesPerView={1.1}
                breakpoints={{
                  560: {
                    slidesPerView: 1.7,
                    grid: {
                      rows: 2,
                      fill: "row",
                    },
                  },
                  730: {
                    slidesPerView: 2.2,
                    grid: {
                      rows: 2,
                      fill: "row",
                    },
                  },
                  1150: {
                    slidesPerView: 3,
                    grid: {
                      rows: 2,
                      fill: "row",
                    },
                  },
                }}
                freeMode={false}
                mousewheel={{
                  forceToAxis: true,
                }}
              >
                {renderCards}
              </Swiper>
            ) : (
              <>{renderCards}</>
            )}
          </>
        </S.CardsWrapper>
        {smallCatCards.length > 2 && (
          <S.NavigationWrapper>
            <S.Navigation onClick={handlePrev} disabled={isBeginning}>
              <FilledArrow className="firstArrow" />
            </S.Navigation>
            <S.Navigation onClick={handleNext} disabled={isEnd}>
              <FilledArrow />
            </S.Navigation>
          </S.NavigationWrapper>
        )}
      </S.Inner>
    </MainSection>
  );
};

export default SubCategoriesCards;
